import {Annotation, ChangeSet, type ChangeSpec, EditorState, type Extension} from '@codemirror/state'
import type {Delimiter} from 'mdast'

import {buildItemContinuationSyntax} from '../../language-tools/markdown/list-utils'
import {MarkdownLine} from '../../language-tools/markdown/markdown-line'
import {indicesOf} from '../../utils'
import {getMarkdownTree, markdownParserExtension} from './markdown-parser'
import {createText} from './single-line-editor'

type EscapeContinuationMode = 'escapeBlockquote' | 'escapeList' | 'all'

/**
 * Annotation to apply to a transaction to indicate that Markdown continuation logic should be skipped to allow the
 * user to escape some Markdown syntax. Will skip up to the last instance of the matching prefix. For example, if
 * the annotation is `escapeList` and the user is inside a blockquote inside a list item, both the blockquote
 * and the list will be escaped.
 */
export const escapeMarkdownContinuation = Annotation.define<EscapeContinuationMode>()

/** Remove prefixes to escape current node based on the `escapeMarkdownContinuation` value. */
function removeEscapedPrefixes(prefixes: readonly Delimiter[], mode: EscapeContinuationMode | undefined) {
  switch (mode) {
    case 'escapeBlockquote':
      return prefixes.slice(
        0,
        prefixes.findLastIndex(prefix => prefix.tokenType === 'blockQuotePrefix'),
      )
    case 'escapeList':
      return prefixes.slice(
        0,
        prefixes.findLastIndex(
          prefix => prefix.tokenType === 'listItemPrefix' || prefix.tokenType === 'listItemIndent',
        ),
      )
    case 'all':
      return []
    case undefined:
      return prefixes
  }
}

/** Returns the syntax that, if prepended to following lines, will continue the nodes at the given offset. */
function buildContinuationSyntax(prefixes: readonly Delimiter[]): string {
  return prefixes
    .map(prefix => {
      switch (prefix.tokenType) {
        case 'listItemPrefix':
          // List items continue as hanging indent matching the prefix size. Note this isn't for creating new items
          // -- that happens on an enter keypress handler. This filter is for continuing the current item, like when
          // pasting into it.
          return buildItemContinuationSyntax(prefix.raw)
        default:
          return prefix.raw
      }
    })
    .join('')
}

/**
 * Transaction filter that maintains the current Markdown node by prepending each line of the inserted text with
 * prefixes that match the current line. Control behavior with `escapeMarkdownContinuation` annotations on the
 * transaction.
 *
 * Using a transaction filter for this (rather than a keybinding) allows us to process pasted, dropped, or otherwise
 * inserted text in addition to simple enter key presses.
 *
 * Currently handles blockquotes, list items, and indented code blocks - all the multiline syntax that is defined with
 * line prefixes (rather than block wrappers).
 */
const markdownContinuationFilter = EditorState.transactionFilter.of(tr => {
  const escapeAnnotation = tr.annotation(escapeMarkdownContinuation)

  // Transaction filters are blocking operations, so we want to early-exit them as fast as possible if we can
  if (!tr.isUserEvent('input') || !tr.docChanged || tr.changes.empty || escapeAnnotation === 'all') return tr

  const startTree = getMarkdownTree(tr.startState)
  const source = tr.startState.doc.toString()

  /** Will be a set of insertions that can be applied after the original changes to add the continuation syntax. */
  const newChanges: ChangeSpec[] = []
  tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
    const text = inserted.toString()

    // Skip all the calculations if the change has no newlines
    if (!text.includes('\n')) return

    const prefixes = removeEscapedPrefixes(
      MarkdownLine.atOffset(startTree, source, fromA)?.prefixes ?? [],
      escapeAnnotation,
    )

    const continuationSyntax = buildContinuationSyntax(prefixes)
    if (continuationSyntax === '') return

    const continuationText = createText(continuationSyntax)
    for (const newlineIndex of indicesOf(text, '\n'))
      newChanges.push({
        from: fromA + newlineIndex + 1,
        insert: continuationText,
      })
  })

  const newChangeSet = ChangeSet.of(newChanges, tr.newDoc.length)
  return [
    tr,
    {
      // These new changes stack on top of the original changes
      sequential: true,
      changes: newChangeSet,
      // Ensure we don't rerun continuation on this transaction
      annotations: [escapeMarkdownContinuation.of('all')],
      // Map the new selection through the additional changes
      // Setting assoc=1 causes the cursor to be associated with inserted text (text following it), thus ending up after it
      selection: tr.newSelection.map(newChangeSet, 1),
    },
  ]
})

/**
 * Automatically continues the current Markdown syntax as the user inserts new lines of content.
 */
export const markdownContinuationExtension: Extension = [markdownParserExtension, markdownContinuationFilter]
